import { useCallback, useMemo, useState } from "react";
import {
    AssignmentStaging,
    PendingAssignment,
    SetPendingEntityParams,
} from "./OrganizationIntegrationsContext";

/**
 * Holds the client picked for an account until its entity is confirmed.
 * Staging is kept for the lifetime of the tab — it survives group-by changes,
 * expand/collapse and pagination, and is dropped only on unmount. Nothing here
 * touches the server, so a reload starts the row over.
 */
export function useAssignmentStaging(): AssignmentStaging {
    const [pendingByAccountId, setPendingByAccountId] = useState<
        Record<number, PendingAssignment>
    >({});

    const setPendingClient = useCallback(
        (accountId: number, workspaceId: string | null) => {
            setPendingByAccountId((previous) => {
                if (workspaceId === null) {
                    if (previous[accountId] === undefined) {
                        return previous;
                    }
                    const next = { ...previous };
                    delete next[accountId];
                    return next;
                }
                if (previous[accountId]?.workspaceId === workspaceId) {
                    return previous;
                }
                return { ...previous, [accountId]: { workspaceId } };
            });
        },
        [],
    );

    const setPendingEntity = useCallback(
        ({ accountIds, workspaceId, entityId }: SetPendingEntityParams) => {
            setPendingByAccountId((previous) => ({
                ...previous,
                ...Object.fromEntries(
                    accountIds.map((accountId) => [
                        accountId,
                        { workspaceId, entityId },
                    ]),
                ),
            }));
        },
        [],
    );

    const clearPendingAccounts = useCallback((accountIds: number[]) => {
        setPendingByAccountId((previous) => {
            const next = { ...previous };
            for (const accountId of accountIds) {
                delete next[accountId];
            }
            return next;
        });
    }, []);

    return useMemo(
        () => ({
            pendingByAccountId,
            setPendingClient,
            setPendingEntity,
            clearPendingAccounts,
        }),
        [
            pendingByAccountId,
            setPendingClient,
            setPendingEntity,
            clearPendingAccounts,
        ],
    );
}
